<template>
  <div
    v-if="isLoadingSymbol"
    class="p-3 w-full flex items-center justify-center"
  >
    <NLoading class="text-brand-primary" />
  </div>

  <div v-else class="px-3 pb-3 w-full rounded-md">
    <div class="flex flex-col">
      <div class="flex justify-between gap-1">
        <div class="inline-flex items-center">
          <SymbolTradeStateIcon
            class="me-2"
            size="medium"
            :trading-state-code="symbol.instrumentTradingState"
          />

          <Retail v-if="getSymbolIsin(symbol)" :isin="getSymbolIsin(symbol)">
            <NText variant="h3" tag="h3" weight="bold">
              {{ symbol.instrumentName }}
            </NText>
          </Retail>
        </div>

        <div class="flex flex-col justify-around">
          <div class="inline-flex items-center">
            <NumberFormatter
              #="{ number }"
              :value="(symbol.referencePriceVariationPercentage ?? 0) / 100"
              maximum-fraction-digits="2"
              sign-display="always"
              number-style="percent"
            >
              <NText
                weight="bold"
                dir="ltr"
                class="mx-1"
                :class="{
                  'text-brand-primary':
                    (symbol.referencePriceVariationPercentage ?? 0) > 0,
                  'text-critical-primary':
                    (symbol.referencePriceVariationPercentage ?? 0) < 0,
                }"
              >
                ({{ number }})
              </NText>
            </NumberFormatter>

            <NumberFormatter #="{ number }" :value="symbol.lastTradePrice">
              <ChangePriceEffect :price="symbol.lastTradePrice ?? 0">
                <NText :key="number" variant="h1" tag="h1" weight="bold">
                  {{ number }}
                </NText>
              </ChangePriceEffect>
            </NumberFormatter>
          </div>
        </div>
      </div>

      <NText variant="caption1" class="text-neutral-secondary">
        {{ symbol.instrumentTitle }}
      </NText>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { getSymbolIsin } from '@core/symbol';

import {
  useLiveSymbolQuery,
  SymbolTradeStateIcon,
  Retail,
} from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

type TSymbolInfoProps = {
  isin: string;
};

const props = defineProps<TSymbolInfoProps>();

const isinRef = ref(props.isin);

const { symbol, isLoadingSymbol } = useLiveSymbolQuery(isinRef);
</script>
